export type CompatibilityStatus = 'supported' | 'limited' | 'missing'

export interface CompatibilityItem {
  id: string
  label: string
  status: CompatibilityStatus
  required: boolean
  detail: string
}

export interface BrowserCompatibilityReport {
  items: CompatibilityItem[]
  blockers: CompatibilityItem[]
  warnings: CompatibilityItem[]
  supported: boolean
}

export interface CompatibilityProbe {
  indexedDb: boolean
  structuredClone: boolean
  canvas2d: boolean
  canvasToBlob: boolean
  objectUrls: boolean
  customFonts: boolean
  storageEstimate: boolean
  persistentStorage: boolean
}

export const evaluateBrowserCompatibility = (probe: CompatibilityProbe): BrowserCompatibilityReport => {
  const items: CompatibilityItem[] = [
    {
      id: 'local-projects',
      label: 'Local project database',
      status: probe.indexedDb ? 'supported' : 'missing',
      required: true,
      detail: probe.indexedDb ? 'Projects can be stored in this browser.' : 'IndexedDB is unavailable, so Deckelier cannot save projects locally.',
    },
    {
      id: 'project-editing',
      label: 'Project editing',
      status: probe.structuredClone ? 'supported' : 'missing',
      required: true,
      detail: probe.structuredClone ? 'Undo, redo, and safe editor snapshots are supported.' : 'This browser cannot safely clone editor state for undo and recovery.',
    },
    {
      id: 'canvas-export',
      label: 'Card rendering and PNG export',
      status: probe.canvas2d && probe.canvasToBlob ? 'supported' : 'missing',
      required: true,
      detail: probe.canvas2d && probe.canvasToBlob ? 'Canvas rendering and image encoding are available.' : 'The required Canvas 2D or PNG encoding support is unavailable.',
    },
    {
      id: 'downloads',
      label: 'Project and print downloads',
      status: probe.objectUrls ? 'supported' : 'missing',
      required: true,
      detail: probe.objectUrls ? 'Generated files can be downloaded.' : 'This browser cannot create downloadable local files.',
    },
    {
      id: 'custom-fonts',
      label: 'Uploaded fonts',
      status: probe.customFonts ? 'supported' : 'limited',
      required: false,
      detail: probe.customFonts ? 'Uploaded font files can be previewed and exported.' : 'Deckelier can run, but uploaded project fonts may not preview or export correctly.',
    },
    {
      id: 'storage-tools',
      label: 'Storage usage and protection',
      status: probe.storageEstimate && probe.persistentStorage ? 'supported' : 'limited',
      required: false,
      detail: probe.storageEstimate && probe.persistentStorage ? 'Storage estimates and eviction protection are available.' : 'Projects still save locally, but storage estimates or browser protection controls are limited.',
    },
  ]
  const blockers = items.filter((item) => item.required && item.status === 'missing')
  const warnings = items.filter((item) => !item.required && item.status !== 'supported')
  return { items, blockers, warnings, supported: blockers.length === 0 }
}

export const detectBrowserCompatibility = (): BrowserCompatibilityReport => {
  const canvas = typeof document === 'undefined' ? null : document.createElement('canvas')
  let canvas2d = false
  try {
    canvas2d = Boolean(canvas?.getContext('2d'))
  } catch {
    canvas2d = false
  }
  return evaluateBrowserCompatibility({
    indexedDb: typeof indexedDB !== 'undefined',
    structuredClone: typeof structuredClone === 'function',
    canvas2d,
    canvasToBlob: typeof canvas?.toBlob === 'function',
    objectUrls: typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function' && typeof URL.revokeObjectURL === 'function',
    customFonts: typeof FontFace !== 'undefined' && typeof document !== 'undefined' && Boolean(document.fonts),
    storageEstimate: typeof navigator !== 'undefined' && typeof navigator.storage?.estimate === 'function',
    persistentStorage: typeof navigator !== 'undefined' && typeof navigator.storage?.persist === 'function' && typeof navigator.storage?.persisted === 'function',
  })
}
